/**
 * API client for MpNucleus backend service.
 * Includes Midway JWT authentication for production deployments.
 */

// API base URL - configure via webpack DefinePlugin or default to local server
declare const NUCLEUS_API_URL: string | undefined;
const API_BASE_URL = (typeof NUCLEUS_API_URL !== 'undefined' ? NUCLEUS_API_URL : null) || 'http://localhost:8085';

// Timeout for API calls (5 seconds)
const API_TIMEOUT = 5000;

// Check if we're running against a production/deployed API
const isProduction = !API_BASE_URL.includes('localhost');

// Cache for Midway JWT token
let midwayJwtToken: string | null = null;
let midwayTokenExpiry: number = 0;

/**
 * Get Midway JWT token for API authentication.
 * Only needed when calling deployed (non-localhost) APIs.
 * 
 * Midway authentication flow (based on MidwayIdentityCredentialProvider):
 * 1. Check if we have a token in query params (after Midway redirect)
 * 2. Try cross-site fetch to /SSO with credentials
 * 3. If that fails, redirect to Midway login page
 * 4. After login, redirect to /SSO/redirect to get token
 */
async function getMidwayJwtToken(): Promise<string | null> {
    // Skip auth for local development
    if (!isProduction) {
        return null;
    }

    // Return cached token if still valid (with 1 minute buffer)
    const now = Date.now();
    if (midwayJwtToken && midwayTokenExpiry > now + 60000) {
        return midwayJwtToken;
    }

    // Step 1: Check if we have a token in query params (after Midway redirect)
    const queryParams = new URLSearchParams(window.location.search);
    const tokenFromQuery = queryParams.get('id_token');
    if (tokenFromQuery && tokenFromQuery.includes('.')) {
        midwayJwtToken = tokenFromQuery;
        
        // Parse token to get expiry time
        try {
            const payloadBase64 = tokenFromQuery.split('.')[1];
            const payload = JSON.parse(atob(payloadBase64));
            midwayTokenExpiry = payload.exp * 1000;
        } catch {
            midwayTokenExpiry = now + 15 * 60 * 1000;
        }
        
        // Clean up URL (remove token from query params)
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('id_token');
        cleanUrl.searchParams.delete('state');
        window.history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search);
        
        console.log('Midway token acquired from redirect');
        return midwayJwtToken;
    }

    // Build Midway request parameters
    // clientId should be the API endpoint (backend), not the frontend hostname
    const clientId = API_BASE_URL.replace(/^https?:\/\//, '').split('/')[0];
    const redirectUri = window.location.href.split('?')[0].split('#')[0];
    const nonce = generateNonce();
    
    const ssoParams = new URLSearchParams({
        response_type: 'id_token',
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: 'openid',
        nonce: nonce,
    });

    // Step 2: Try cross-site fetch to /SSO (works if user already has Midway session)
    try {
        const ssoUrl = `https://midway-auth.amazon.com/SSO?${ssoParams.toString()}`;
        const response = await fetch(ssoUrl, { credentials: 'include' });
        if (response.ok) {
            const token = await response.text();
            if (token && token.includes('.')) {
                midwayJwtToken = token;
                try {
                    const payloadBase64 = token.split('.')[1];
                    const payload = JSON.parse(atob(payloadBase64));
                    midwayTokenExpiry = payload.exp * 1000;
                } catch {
                    midwayTokenExpiry = now + 15 * 60 * 1000;
                }
                console.log('Midway token acquired from cross-site call');
                return midwayJwtToken;
            }
        }
    } catch (error) {
        console.log('Cross-site Midway call failed (expected if no session):', error);
    }

    // Step 3: Check if we're coming back from Midway login
    const fromMidway = document.referrer.includes('midway-auth.amazon.com');
    
    if (!fromMidway) {
        // Not from Midway - redirect to login page first
        const ssoRedirectUri = `/SSO/redirect?${ssoParams.toString()}`;
        const loginUrl = `https://midway-auth.amazon.com/login?next=${encodeURIComponent(ssoRedirectUri)}`;
        console.log('Redirecting to Midway login page...');
        window.location.assign(loginUrl);
    } else {
        // Already logged in but no token - use SSO/redirect
        const ssoRedirectUrl = `https://midway-auth.amazon.com/SSO/redirect?${ssoParams.toString()}`;
        console.log('Redirecting to Midway SSO redirect...');
        window.location.assign(ssoRedirectUrl);
    }
    
    // This promise will never resolve because we're redirecting
    return new Promise(() => {});
}

/**
 * Generate a secure nonce for Midway authentication
 */
function generateNonce(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const base64 = btoa(Array.from(array).map(b => String.fromCharCode(b)).join(''))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    return base64.substring(0, 64);
}

/**
 * Make an authenticated API request.
 * Automatically includes Midway JWT token for production deployments.
 */
async function fetchWithAuth(url: string, options: RequestInit = {}, timeout: number = API_TIMEOUT): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        };

        // Add Authorization header for production deployments
        if (isProduction) {
            const token = await getMidwayJwtToken();
            if (token) {
                (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            } else {
                // If we can't get a token, try anyway - API Gateway will return 401
                console.warn('No Midway JWT token available - API call may fail');
            }
        }

        const response = await fetch(url, {
            ...options,
            headers,
            signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        // Handle authentication errors
        if (response.status === 401) {
            console.error('Authentication failed - redirecting to Midway');
            // Clear cached token
            midwayJwtToken = null;
            midwayTokenExpiry = 0;
            // Could redirect to Midway here if needed
            throw new Error('Authentication required - please ensure you are logged in to Midway');
        }
        
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out - backend may not be running');
        }
        throw error;
    }
}

// Legacy fetch function for backwards compatibility
async function fetchWithTimeout(url: string, timeout: number = API_TIMEOUT): Promise<Response> {
    return fetchWithAuth(url, {}, timeout);
}

export interface RunMetadata {
    runId: string;
    submittedBy?: string;
    submittedAt?: string;
    description?: string;
    status?: string;
    pipelineStages?: string[];
    simulationCount?: number;
    notes?: string;
}

export interface FileInfo {
    key: string;
    name: string;
    size: number;
    lastModified: string;
}

export interface ListRunsResponse {
    runs: RunMetadata[];
}

export interface ListFilesResponse {
    folders: string[];
    files: FileInfo[];
}

export interface PresignedUrlResponse {
    url: string;
    expiresIn: number;
}

/**
 * Fetch all simulation runs with metadata.
 */
export async function listRuns(): Promise<ListRunsResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/listRuns`);
    if (!response.ok) {
        throw new Error(`Failed to list runs: ${response.statusText}`);
    }
    return response.json();
}

/**
 * List files and folders at a given S3 prefix.
 */
export async function listFiles(prefix: string): Promise<ListFilesResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/listFiles?prefix=${encodeURIComponent(prefix)}`);
    if (!response.ok) {
        throw new Error(`Failed to list files: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get a presigned URL for downloading a file.
 */
export async function getPresignedUrl(key: string): Promise<PresignedUrlResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/getPresignedUrl?key=${encodeURIComponent(key)}`);
    if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Download file content directly using presigned URL.
 * Note: Presigned URLs don't need JWT auth - they are pre-authorized.
 */
export async function downloadFile(key: string): Promise<string> {
    const { url } = await getPresignedUrl(key);
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
    }
    return response.text();
}

/**
 * Check if the user is authenticated with Midway.
 * Useful for UI to show login status.
 */
export async function checkMidwayAuth(): Promise<boolean> {
    if (!isProduction) {
        return true; // Local development doesn't need auth
    }
    
    try {
        const token = await getMidwayJwtToken();
        return token !== null;
    } catch {
        return false;
    }
}

/**
 * Get the current authenticated user's alias from the Midway token.
 */
export function getCurrentUser(): string | null {
    if (!midwayJwtToken) {
        return null;
    }
    
    try {
        const payloadBase64 = midwayJwtToken.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        return payload.sub || null;
    } catch {
        return null;
    }
}

/**
 * Check if we're running in production mode (against deployed API).
 */
export function isProductionMode(): boolean {
    return isProduction;
}
